import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import ReactMarkdown from 'react-markdown';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import Button from '~/app/ui/Button';
import Heatmap from './Heatmap';

// Types
interface PageMetrics {
  views: {
    total_views: number;
    unique_sessions: number;
    unique_visitors: number;
    views_by_date: Record<string, number>;
    views_by_device: Record<string, number>;
    views_by_country: Record<string, number>;
  };
  duration: {
    average_duration_seconds: number;
    average_duration_formatted: string;
  };
  scroll: {
    average_max_depth: number;
    milestone_percentages: Record<string, number>;
  };
  clicks: {
    total_clicks: number;
    top_elements: Array<{
      element: string;
      clicks: number;
      text: string;
    }>;
  };
  traffic_sources?: {
    total_sessions: number;
    sources: Record<string, number>;
    source_percentages: Record<string, number>;
    top_referrers: Array<{ referrer: string; count: number }>;
  };
  all_versions?: {
    total_views: number;
    unique_sessions: number;
    unique_visitors: number;
    average_duration_seconds: number;
    average_duration_formatted: string;
    average_max_depth: number;
  };
}

interface DateRange {
  start_date: string;
  end_date: string;
}

type PresetRange = '7' | '30' | '90' | 'today' | 'yesterday' | 'custom';

// Device filter type for heatmap
type DeviceFilter = 'all' | 'desktop' | 'mobile' | 'tablet';

// Device viewport presets for responsive heatmap preview
// Website breakpoints: Mobile 0-599px, Tablet 600-899px, Desktop 900px+
const DEVICE_VIEWPORTS: Record<DeviceFilter, { width: number; label: string; icon: string }> = {
  all: { width: 0, label: 'All Devices', icon: 'devices' },
  desktop: { width: 1200, label: 'Desktop', icon: 'computer' },
  tablet: { width: 768, label: 'Tablet', icon: 'tablet' },
  mobile: { width: 390, label: 'Mobile', icon: 'smartphone' },
};

const HEATMAP_CLICK_LIMITS = {
  allVersions: 600,
  singleVersion: 1000,
};

// GraphQL query to get page info
const GET_PAGE = gql`
  query getPage($id: ID!) {
    page(id: $id) {
      _id
      title
      path
      publishedVersion
    }
  }
`;

// Helper to format date for datetime-local input
const formatDateTimeLocal = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

// Helper to format date for display
const formatDisplayDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Helper to convert country code to flag emoji
const getFlagEmoji = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Helper to convert country code to country name
const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  CN: 'China',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  JP: 'Japan',
  CA: 'Canada',
  AU: 'Australia',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  KR: 'South Korea',
  IT: 'Italy',
  ES: 'Spain',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  PL: 'Poland',
  RU: 'Russia',
  SG: 'Singapore',
  HK: 'Hong Kong',
  TW: 'Taiwan',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  IL: 'Israel',
  ZA: 'South Africa',
  NZ: 'New Zealand',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  ID: 'Indonesia',
  TH: 'Thailand',
  MY: 'Malaysia',
  PH: 'Philippines',
  VN: 'Vietnam',
  IE: 'Ireland',
  PT: 'Portugal',
  CZ: 'Czech Republic',
  RO: 'Romania',
  HU: 'Hungary',
  GR: 'Greece',
  TR: 'Turkey',
  EG: 'Egypt',
  NG: 'Nigeria',
  KE: 'Kenya',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  UA: 'Ukraine',
};

const getCountryName = (code: string): string => {
  return COUNTRY_NAMES[code?.toUpperCase()] || code || 'Unknown';
};

const PageAnalytics: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Get version from query params
  const queryParams = new URLSearchParams(location.search);
  const versionParam = queryParams.get('version');

  // Initialize dates
  const getInitialDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      start: formatDateTimeLocal(start),
      end: formatDateTimeLocal(end),
    };
  };

  // State
  const [metrics, setMetrics] = useState<PageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [presetRange, setPresetRange] = useState<PresetRange>('7');
  // Input state (for the form inputs - changes as user types)
  const [customStartDate, setCustomStartDate] = useState<string>(getInitialDates().start);
  const [customEndDate, setCustomEndDate] = useState<string>(getInitialDates().end);
  // Applied state (only changes when user presses Apply - used for data fetching)
  const [appliedStartDate, setAppliedStartDate] = useState<string>(getInitialDates().start);
  const [appliedEndDate, setAppliedEndDate] = useState<string>(getInitialDates().end);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'heatmap'>('overview');
  const [chatMessages, setChatMessages] = useState<Array<{ role: string; content: string; logId?: string }>>(
    []
  );
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [clicksData, setClicksData] = useState<Array<any>>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<number, string>>({});
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('desktop');
  const [clicksByDevice, setClicksByDevice] = useState<Record<string, number>>({});
  const [versionScope, setVersionScope] = useState<'this' | 'all'>('this'); // Toggle for version filtering

  // --- Autosizing chat input (ChatGPT-style) ---
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const CHAT_INPUT_MIN_HEIGHT = 48; // px (normal size)
  const CHAT_INPUT_MAX_HEIGHT = 180; // px (cap growth, then scroll)

  const resizeChatInput = useCallback(() => {
    const el = chatTextareaRef.current;
    if (!el) return;

    // Reset so scrollHeight is accurate
    el.style.height = 'auto';

    const next = Math.min(el.scrollHeight, CHAT_INPUT_MAX_HEIGHT);
    const clamped = Math.max(next, CHAT_INPUT_MIN_HEIGHT);

    el.style.height = `${clamped}px`;
    el.style.overflowY = el.scrollHeight > CHAT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeChatInput();
  }, [chatInput, resizeChatInput]);

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Avoid submitting while IME composing (JP/CN/KR input, etc.)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((e.nativeEvent as any)?.isComposing) return;

    // Enter submits, Shift+Enter inserts newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (chatLoading || !chatInput.trim()) return;

      // Submit the surrounding form
      const form = e.currentTarget.form;
      form?.requestSubmit?.();
    }
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get page info
  const { data: pageData } = useQuery(GET_PAGE, {
    variables: { id },
  });

  const page = pageData?.page;

  // For CXC10/AIXC pages, transform path to match actual deployed URL on aixcrypto.ai
  // In Genesis: /cxc10/en/page-name -> On aixcrypto.ai: /en/page-name
  const getAnalyticsPath = (pagePath: string): string => {
    if (!pagePath) return pagePath;
    if (pagePath.startsWith('/cxc10')) {
      return pagePath.replace(/^\/cxc10/, '');
    }
    return pagePath;
  };

  // Helper to transform cxc10 to aixc for display purposes
  const transformPathDisplay = (pathStr: string): string => {
    if (!pathStr) return pathStr;
    return pathStr.replace(/\/cxc10/g, '/aixc');
  };

  const analyticsPath = page?.path ? getAnalyticsPath(page.path) : null;

  // Use version from URL or fall back to published version
  const selectedVersion = versionParam || page?.publishedVersion;
  
  // Determine the effective version for API calls based on scope toggle
  // If "All Versions" is selected, pass null to get data for all versions
  // If "This Version" is selected, pass the selected version to filter
  const effectiveVersion = versionScope === 'all' ? null : selectedVersion;

  // Calculate date range based on preset or custom dates
  // Uses appliedStartDate/appliedEndDate for custom ranges (only updated on Apply button click)
  const getDateRange = useCallback((): DateRange => {
    if (presetRange === 'custom') {
      return {
        start_date: appliedStartDate,
        end_date: appliedEndDate,
      };
    }

    const now = new Date();
    let start: Date;
    let end: Date;

    switch (presetRange) {
      case 'today':
        // Today: from midnight today to end of today (23:59:59)
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'yesterday':
        // Yesterday: from midnight yesterday to end of yesterday (23:59:59)
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
        break;
      case '7':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case '30':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case '90':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }

    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
  }, [presetRange, appliedStartDate, appliedEndDate]);

  // Get display text for current date range
  // Uses applied dates for custom range display (reflects what data is actually being shown)
  const getDateRangeDisplay = (): string => {
    switch (presetRange) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case '7':
        return 'Last 7 days';
      case '30':
        return 'Last 30 days';
      case '90':
        return 'Last 90 days';
      case 'custom': {
        const startDisplay = formatDisplayDate(appliedStartDate);
        const endDisplay = formatDisplayDate(appliedEndDate);
        return `${startDisplay} - ${endDisplay}`;
      }
      default:
        return 'Select date range';
    }
  };

  // Handle preset selection
  const handlePresetSelect = (preset: PresetRange) => {
    setPresetRange(preset);
    if (preset !== 'custom') {
      setShowDatePicker(false);
    }
  };

  // Handle custom date application - only updates data when user explicitly applies
  const handleApplyCustomDates = () => {
    // Update applied state with current input values - this triggers data fetch
    setAppliedStartDate(customStartDate);
    setAppliedEndDate(customEndDate);
    setPresetRange('custom');
    setShowDatePicker(false);
  };

  // Fetch analytics data
  const fetchMetrics = useCallback(async () => {
    if (!analyticsPath) return;

    setLoading(true);
    setError(null);

    try {
      const { start_date, end_date } = getDateRange();

      const response = await fetch('/api/analytics-agent/pages/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: analyticsPath,
          start_date,
          end_date,
          version: effectiveVersion,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();

      if (data.status === 'success') {
        setMetrics(data.data);
      } else {
        setError(data.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [analyticsPath, getDateRange, effectiveVersion, versionScope]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Fetch click data for heatmap
  const fetchClicksData = useCallback(async () => {
    if (!analyticsPath) return;

    setClicksLoading(true);
    try {
      const { start_date, end_date } = getDateRange();

      const response = await fetch('/api/analytics-agent/pages/clicks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: analyticsPath,
          start_date,
          end_date,
          version: effectiveVersion,
          limit: versionScope === 'all' ? HEATMAP_CLICK_LIMITS.allVersions : HEATMAP_CLICK_LIMITS.singleVersion,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setClicksData(data.data.clicks || []);
          setClicksByDevice(data.data.clicks_by_device || {});
        }
      }
    } catch (err) {
      console.error('Failed to fetch clicks data:', err);
    } finally {
      setClicksLoading(false);
    }
  }, [analyticsPath, getDateRange, effectiveVersion, versionScope]);

  // Filter clicks by selected device
  const filteredClicksData =
    deviceFilter === 'all' ? clicksData : clicksData.filter((click) => click.device === deviceFilter);

  // Fetch clicks data when switching to heatmap tab
  useEffect(() => {
    if (activeTab === 'heatmap') {
      fetchClicksData();
    }
  }, [activeTab, fetchClicksData]);

  // Chat with AI
  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const { start_date, end_date } = getDateRange();

      const response = await fetch('/api/analytics-agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `For page ${analyticsPath || page?.path}: ${userMessage}`,
          conversation_history: chatMessages,
          // Pass version and date range so AI filters data correctly
          version: selectedVersion,
          start_date,
          end_date,
        }),
      });

      const data = await response.json();

      // Handle API errors (non-2xx responses)
      if (!response.ok || !data.response) {
        throw new Error(data.detail || data.error || 'Failed to get response');
      }

      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          logId: data.log_id || undefined,
        },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Submit feedback for AI response
  const handleFeedback = async (messageIndex: number, feedbackType: 'thumbs_up' | 'thumbs_down') => {
    if (feedbackGiven[messageIndex]) return; // Already gave feedback

    // Get the log_id and content from the message itself
    const message = chatMessages[messageIndex];
    const logId = message?.logId || `msg-${messageIndex}`;

    // Find the user's question that preceded this response
    // User message should be at messageIndex - 1
    const userMessage = messageIndex > 0 ? chatMessages[messageIndex - 1] : null;
    const question = userMessage?.role === 'user' ? userMessage.content : null;
    const response = message?.content;

    try {
      await fetch('/api/analytics-agent/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          log_id: logId,
          feedback_type: feedbackType,
          // Include question/response for distributed environments (multi-pod)
          question: question,
          response: response,
          page_path: analyticsPath || page?.path,
        }),
      });

      setFeedbackGiven((prev) => ({ ...prev, [messageIndex]: feedbackType }));
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  if (!page) {
    return (
      <div className={classes.container}>
        <div className={classes.loading}>
          <i className="material-icons">hourglass_empty</i>
          <span>Loading page info...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <button
            className={classes.backButton}
            onClick={() => history.push(`/admin/page/${id}`)}
            title="Back to Editor"
          >
            <i className="material-icons">arrow_back</i>
          </button>
          <div className={classes.headerInfo}>
            <h1 className={classes.pageTitle}>{page.title}</h1>
            <div className={classes.pagePath}>
              <span>{transformPathDisplay(page.path)}</span>
              {selectedVersion && <span className={classes.versionTag}>v{selectedVersion}</span>}
            </div>
          </div>
        </div>
        <div className={classes.headerRight}>
          {/* Date Range Picker */}
          <div className={classes.datePickerWrapper} ref={datePickerRef}>
            <button
              className={classes.datePickerToggle}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <i className="material-icons">date_range</i>
              <span className={classes.dateRangeText}>{getDateRangeDisplay()}</span>
              <i className="material-icons">{showDatePicker ? 'expand_less' : 'expand_more'}</i>
            </button>

            {showDatePicker && (
              <div className={classes.datePickerDropdown}>
                <div className={classes.presetSection}>
                  <div className={classes.presetLabel}>Quick Select</div>
                  <div className={classes.presetGrid}>
                    <button
                      className={`${classes.presetBtn} ${
                        presetRange === 'today' ? classes.presetActive : ''
                      }`}
                      onClick={() => handlePresetSelect('today')}
                    >
                      Today
                    </button>
                    <button
                      className={`${classes.presetBtn} ${
                        presetRange === 'yesterday' ? classes.presetActive : ''
                      }`}
                      onClick={() => handlePresetSelect('yesterday')}
                    >
                      Yesterday
                    </button>
                    <button
                      className={`${classes.presetBtn} ${
                        presetRange === '7' ? classes.presetActive : ''
                      }`}
                      onClick={() => handlePresetSelect('7')}
                    >
                      Last 7 days
                    </button>
                    <button
                      className={`${classes.presetBtn} ${
                        presetRange === '30' ? classes.presetActive : ''
                      }`}
                      onClick={() => handlePresetSelect('30')}
                    >
                      Last 30 days
                    </button>
                    <button
                      className={`${classes.presetBtn} ${
                        presetRange === '90' ? classes.presetActive : ''
                      }`}
                      onClick={() => handlePresetSelect('90')}
                    >
                      Last 90 days
                    </button>
                  </div>
                </div>

                <div className={classes.customSection}>
                  <div className={classes.customLabel}>Custom Range</div>
                  <div className={classes.dateInputGroup}>
                    <div className={classes.dateInputWrapper}>
                      <label className={classes.dateInputLabel}>Start Date & Time</label>
                      <input
                        type="datetime-local"
                        className={classes.dateInput}
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                      />
                    </div>
                    <div className={classes.dateInputWrapper}>
                      <label className={classes.dateInputLabel}>End Date & Time</label>
                      <input
                        type="datetime-local"
                        className={classes.dateInput}
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button variant="primary" className={classes.applyBtn} onClick={handleApplyCustomDates}>
                    Apply custom range
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Version Scope Toggle */}
          {selectedVersion && (
            <div className={classes.versionScopeToggle}>
              <div className={classes.versionScopeLabel}>
                <i className="material-icons">history</i>
                <span>Version:</span>
              </div>
              <div className={classes.versionScopeButtons}>
                <button
                  className={`${classes.versionScopeBtn} ${
                    versionScope === 'this' ? classes.versionScopeActive : ''
                  }`}
                  onClick={() => setVersionScope('this')}
                  title={`Show data for version ${selectedVersion} only`}
                >
                  <i className="material-icons">filter_1</i>
                  This Version (v{selectedVersion})
                </button>
                <button
                  className={`${classes.versionScopeBtn} ${
                    versionScope === 'all' ? classes.versionScopeActive : ''
                  }`}
                  onClick={() => setVersionScope('all')}
                  title="Show data for all versions of this page"
                >
                  <i className="material-icons">dynamic_feed</i>
                  All Versions
                </button>
              </div>
            </div>
          )}

          <button className={classes.refreshButton} onClick={fetchMetrics} title="Refresh Data">
            <i className="material-icons">refresh</i>
          </button>
          <Link to={`/admin/page/${id}`} className={classes.editButton}>
            <i className="material-icons">edit</i>
            Edit Page
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={classes.tabBar}>
        <button
          className={classes.tab}
          data-active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          <i className="material-icons">dashboard</i>
          Overview
        </button>
        <button
          className={classes.tab}
          data-active={activeTab === 'heatmap'}
          onClick={() => setActiveTab('heatmap')}
        >
          <i className="material-icons">layers</i>
          Heatmap
        </button>
      </div>

      {/* Main Content Area */}
      <div className={classes.contentWrapper}>
        <div className={classes.mainContent}>
          {loading ? (
            <div className={classes.loading}>
              <div className={classes.spinner} />
              <span>Loading analytics data...</span>
            </div>
          ) : error ? (
            <div className={classes.errorState}>
              <i className="material-icons">error_outline</i>
              <p>{error}</p>
              <Button icon="refresh" onClick={fetchMetrics}>Retry</Button>
            </div>
          ) : metrics && activeTab === 'overview' ? (
            <>
              {/* Stats Row */}
              <div className={classes.statsRow}>
                {/* Views Card - Label changes based on scope */}
                <div className={classes.statCard}>
                  <div className={classes.statIcon}>
                    {versionScope === 'all' ? (
                      <i className="material-icons">layers</i>
                    ) : (
                      <i className="material-icons">visibility</i>
                    )}
                  </div>
                  <div className={classes.statContent}>
                    <span className={classes.statLabel}>
                      {versionScope === 'all' ? 'Views (All Versions)' : 'Views (This Version)'}
                    </span>
                    <span className={classes.statValue}>{formatNumber(metrics.views.total_views)}</span>
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statIcon}>
                    <i className="material-icons">people</i>
                  </div>
                  <div className={classes.statContent}>
                    <span className={classes.statLabel}>
                      Unique Sessions
                      <span className={classes.tooltipWrapper}>
                        <i 
                          className="material-icons"
                          style={{ fontSize: '14px', marginLeft: '4px', cursor: 'pointer', opacity: 0.6 }}
                        >
                          info_outline
                        </i>
                        <span className={classes.tooltip}>
                          Distinct browsing sessions that viewed this page. Each page load creates a new session, so one person may have multiple sessions.
                        </span>
                      </span>
                    </span>
                    <span className={classes.statValue}>
                      {formatNumber(metrics.views.unique_sessions)}
                    </span>
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statIcon}>
                    <i className="material-icons">people</i>
                  </div>
                  <div className={classes.statContent}>
                    <span className={classes.statLabel}>
                      Unique Visitors
                      <span className={classes.tooltipWrapper}>
                        <i 
                          className="material-icons"
                          style={{ fontSize: '14px', marginLeft: '4px', cursor: 'pointer', opacity: 0.6 }}
                        >
                          info_outline
                        </i>
                        <span className={classes.tooltip}>
                          Distinct individuals who viewed this page, tracked via browser storage. More accurate than sessions for counting real people.
                        </span>
                      </span>
                    </span>
                    <span className={classes.statValue}>
                      {formatNumber(metrics.views.unique_visitors || 0)}
                    </span>
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statIcon}>
                    <i className="material-icons">schedule</i>
                  </div>
                  <div className={classes.statContent}>
                    <span className={classes.statLabel}>Avg. Time</span>
                    <span className={classes.statValue}>{metrics.duration.average_duration_formatted}</span>
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statIcon}>
                    <i className="material-icons">swap_vert</i>
                  </div>
                  <div className={classes.statContent}>
                    <span className={classes.statLabel}>Scroll Depth</span>
                    <span className={classes.statValue}>{metrics.scroll.average_max_depth}%</span>
                  </div>
                </div>
              </div>

              {/* Charts Grid */}
              <div className={classes.chartsGrid}>
                {/* Daily Views Chart */}
                <div className={classes.chartPanel}>
                  <div className={classes.panelHeader}>
                    <span className={classes.panelTitle}>Daily Views</span>
                  </div>
                  <div className={classes.panelBody}>
                    {metrics.views.views_by_date && Object.keys(metrics.views.views_by_date).length > 0 ? (
                      <>
                        {Object.entries(metrics.views.views_by_date)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .slice(-14)
                          .map(([date, count]) => {
                            const maxCount = Math.max(...Object.values(metrics.views.views_by_date));
                            const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                            return (
                              <div key={date} className={classes.chartRow}>
                                <span className={classes.chartLabel}>{date.slice(5)}</span>
                                <div className={classes.chartBarBg}>
                                  <div className={classes.chartBar} style={{ width: `${percentage}%` }} />
                                </div>
                                <span className={classes.chartValue}>{count}</span>
                              </div>
                            );
                          })}
                      </>
                    ) : (
                      <div className={classes.emptyState}>
                        <i className="material-icons">bar_chart</i>
                        <span>No daily views data available</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Device Breakdown */}
                <div className={classes.chartPanel}>
                  <div className={classes.panelHeader}>
                    <span className={classes.panelTitle}>Device Breakdown</span>
                  </div>
                  <div className={classes.panelBody}>
                    {metrics.views.views_by_device && Object.keys(metrics.views.views_by_device).length > 0 ? (
                      <>
                        {Object.entries(metrics.views.views_by_device).map(([device, count]) => {
                          const percentage =
                            metrics.views.total_views > 0
                              ? Math.round((count / metrics.views.total_views) * 100)
                              : 0;
                          const icon =
                            device === 'desktop' ? 'computer' : device === 'mobile' ? 'smartphone' : 'tablet';
                          return (
                            <div key={device} className={classes.deviceRow}>
                              <div className={classes.deviceInfo}>
                                <i className="material-icons">{icon}</i>
                                <span className={classes.deviceName}>{device}</span>
                              </div>
                              <div className={classes.deviceBarBg}>
                                <div className={classes.deviceBar} style={{ width: `${percentage}%` }} />
                              </div>
                              <span className={classes.deviceStats}>
                                <span className={classes.deviceCount}>{formatNumber(count)}</span>
                                <span className={classes.devicePct}>{percentage}%</span>
                              </span>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div className={classes.emptyState}>
                        <i className="material-icons">devices</i>
                        <span>No device data available</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Scroll Depth */}
                <div className={classes.chartPanel}>
                  <div className={classes.panelHeader}>
                    <span className={classes.panelTitle}>Scroll Depth</span>
                  </div>
                  <div className={classes.panelBody}>
                    {[25, 50, 75, 100].map((milestone) => {
                      const pct = metrics.scroll.milestone_percentages[milestone] || 0;
                      return (
                        <div key={milestone} className={classes.scrollRow}>
                          <span className={classes.scrollMilestone}>{milestone}%</span>
                          <div className={classes.scrollBarBg}>
                            <div className={classes.scrollBar} style={{ width: `${pct}%` }} />
                          </div>
                          <span className={classes.scrollPct}>{pct}%</span>
                        </div>
                      );
                    })}
                    <div className={classes.panelNote}>Percentage of visitors reaching each scroll milestone</div>
                  </div>
                </div>

                {/* Country Distribution */}
                <div className={classes.chartPanel}>
                  <div className={classes.panelHeader}>
                    <span className={classes.panelTitle}>Visitors by Country</span>
                  </div>
                  <div className={classes.panelBody}>
                    {metrics.views.views_by_country && Object.keys(metrics.views.views_by_country).length > 0 ? (
                      <>
                        {Object.entries(metrics.views.views_by_country)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 8)
                          .map(([country, count]) => {
                            const percentage =
                              metrics.views.total_views > 0
                                ? Math.round((count / metrics.views.total_views) * 100)
                                : 0;
                            return (
                              <div key={country} className={classes.countryRow}>
                                <div className={classes.countryInfo}>
                                  <span className={classes.countryFlag}>
                                    {country === 'Unknown' ? '🌍' : getFlagEmoji(country)}
                                  </span>
                                  <span className={classes.countryName}>
                                    {country === 'Unknown' ? 'Unknown' : getCountryName(country)}
                                  </span>
                                </div>
                                <div className={classes.countryBarBg}>
                                  <div className={classes.countryBar} style={{ width: `${percentage}%` }} />
                                </div>
                                <span className={classes.countryStats}>
                                  <span className={classes.countryCount}>{formatNumber(count)}</span>
                                  <span className={classes.countryPct}>{percentage}%</span>
                                </span>
                              </div>
                            );
                          })}
                      </>
                    ) : (
                      <div className={classes.emptyState}>
                        <i className="material-icons">public</i>
                        <span>No country data available</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Traffic Sources */}
                <div className={classes.chartPanel}>
                  <div className={classes.panelHeader}>
                    <span className={classes.panelTitle}>Traffic Sources</span>
                    {metrics.traffic_sources && (
                      <span className={classes.panelBadge}>{formatNumber(metrics.traffic_sources.total_sessions)} sessions</span>
                    )}
                  </div>
                  <div className={classes.panelBody}>
                    {metrics.traffic_sources && metrics.traffic_sources.sources ? (
                      <>
                        {['direct', 'search', 'social', 'referral'].map((source) => {
                          const count = metrics.traffic_sources?.sources[source] || 0;
                          const percentage = metrics.traffic_sources?.source_percentages[source] || 0;
                          const icons: Record<string, string> = {
                            direct: 'link',
                            search: 'search',
                            social: 'share',
                            referral: 'open_in_new',
                          };
                          const labels: Record<string, string> = {
                            direct: 'Direct',
                            search: 'Search',
                            social: 'Social',
                            referral: 'Referral',
                          };
                          return (
                            <div key={source} className={classes.sourceRow}>
                              <div className={classes.sourceInfo}>
                                <i className="material-icons">{icons[source]}</i>
                                <span className={classes.sourceName}>{labels[source]}</span>
                              </div>
                              <div className={classes.sourceBarBg}>
                                <div className={classes.sourceBar} style={{ width: `${percentage}%` }} />
                              </div>
                              <span className={classes.sourceStats}>
                                <span className={classes.sourceCount}>{formatNumber(count)}</span>
                                <span className={classes.sourcePct}>{percentage}%</span>
                              </span>
                            </div>
                          );
                        })}
                        {metrics.traffic_sources.top_referrers &&
                          metrics.traffic_sources.top_referrers.length > 0 && (
                            <div className={classes.topReferrers}>
                              <div className={classes.referrerLabel}>Top Referrers</div>
                              {metrics.traffic_sources.top_referrers.slice(0, 5).map((ref, idx) => (
                                <div key={idx} className={classes.referrerItem}>
                                  <span className={classes.referrerDomain}>{ref.referrer}</span>
                                  <span className={classes.referrerCount}>{ref.count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                      </>
                    ) : (
                      <div className={classes.emptyState}>
                        <i className="material-icons">traffic</i>
                        <span>No traffic source data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : activeTab === 'heatmap' ? (
            <>
              <div className={classes.chartPanel}>
                <div className={classes.panelHeader}>
                  <span className={classes.panelTitle}>Click Heatmap</span>
                  <span className={classes.panelBadge}>{filteredClicksData.length} clicks</span>
                </div>
                <div className={classes.panelBody}>
                  {/* Device Filter */}
                  <div className={classes.deviceFilterSection}>
                    <div className={classes.deviceFilterLabel}>
                      <i className="material-icons">devices</i>
                      <span>Device View</span>
                    </div>
                    <div className={classes.deviceFilterButtons}>
                      {(Object.keys(DEVICE_VIEWPORTS) as DeviceFilter[]).map((device) => {
                        const config = DEVICE_VIEWPORTS[device];
                        const count = device === 'all' ? clicksData.length : clicksByDevice[device] || 0;
                        return (
                          <button
                            key={device}
                            className={`${classes.deviceFilterBtn} ${
                              deviceFilter === device ? classes.deviceFilterActive : ''
                            }`}
                            onClick={() => setDeviceFilter(device)}
                            title={`${config.label} (${count} clicks)`}
                          >
                            <i className="material-icons">{config.icon}</i>
                            <span className={classes.deviceFilterBtnLabel}>{config.label}</span>
                            <span className={classes.deviceFilterBtnCount}>{count}</span>
                          </button>
                        );
                      })}
                    </div>
                    {deviceFilter === 'all' && clicksData.length > 0 && (
                      <div className={classes.deviceFilterWarning}>
                        <i className="material-icons">warning</i>
                        <span>
                          Showing all devices may produce inaccurate results due to different viewport sizes. Select a
                          specific device for accurate visualization.
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={classes.panelNote} style={{ marginBottom: '15px' }}>
                    Visual representation of where users click on this page. Warmer colors indicate higher click density.
                    {versionScope === 'all' && (
                      <>
                        {' '}
                        To keep this view responsive, All Versions heatmaps show a capped data sample.
                      </>
                    )}
                  </div>

                  {clicksLoading ? (
                    <div className={classes.loadingState}>
                      <div className={classes.spinner} />
                      <span>Loading click data...</span>
                    </div>
                  ) : filteredClicksData.length > 0 ? (
                    <Heatmap path={page.path} clicks={filteredClicksData} version={selectedVersion} deviceFilter={deviceFilter} />
                  ) : (
                    <div className={classes.emptyState}>
                      <i className="material-icons">layers_clear</i>
                      <span>
                        {clicksData.length > 0
                          ? `No click data available for ${DEVICE_VIEWPORTS[deviceFilter].label.toLowerCase()}`
                          : 'No click data available for heatmap visualization'}
                      </span>
                      <span className={classes.emptySubtext}>
                        {clicksData.length > 0 ? 'Try selecting a different device type' : 'Make sure the analytics tracker is collecting data'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={classes.emptyState}>
              <i className="material-icons">info_outline</i>
              <span>No analytics data available</span>
            </div>
          )}
        </div>

        {/* AI Assistant - Always Visible */}
        <div className={classes.aiPanel}>
          <div className={classes.aiHeader}>
            <div className={classes.aiHeaderLeft}>
              <div className={classes.aiHeaderIcon}>
                <i className="material-icons">{activeTab === 'overview' ? 'psychology' : 'touch_app'}</i>
              </div>
              <div className={classes.aiHeaderTitle}>
                <h4>{activeTab === 'overview' ? 'Analytics Assistant' : 'Click Pattern Analysis'}</h4>
                <span>Powered by FFAI</span>
              </div>
            </div>
            <div className={classes.aiHeaderActions}>
              {chatMessages.length > 0 && (
                <button
                  className={classes.aiHeaderBtn}
                  onClick={() => {
                    setChatMessages([]);
                    setFeedbackGiven({}); // Clear feedback state for new conversation
                  }}
                  title="New conversation"
                >
                  <i className="material-icons">refresh</i>
                </button>
              )}
            </div>
          </div>

          <div className={classes.aiMessages}>
            {chatMessages.length === 0 && (
              <div className={classes.aiWelcome}>
                <div className={classes.welcomeIconWrap}>
                  <i className="material-icons">{activeTab === 'overview' ? 'auto_awesome' : 'touch_app'}</i>
                </div>
                <h3 className={classes.welcomeTitle}>
                  {activeTab === 'overview' ? 'How can I help you today?' : 'Analyze Click Patterns'}
                </h3>
                <p className={classes.welcomeSubtitle}>
                  {activeTab === 'overview'
                    ? "I can analyze your page's performance, identify trends, and provide actionable insights."
                    : 'I can help you understand where users click and identify areas to optimize.'}
                </p>
                <div className={classes.aiSuggestions}>
                  <span className={classes.suggestionLabel}>Quick questions</span>
                  {activeTab === 'overview' ? (
                    <>
                      <button className={classes.suggestionBtn} onClick={() => setChatInput('What is the trend in page views?')}>
                        What is the trend in page views?
                      </button>
                      <button className={classes.suggestionBtn} onClick={() => setChatInput('How can I improve engagement?')}>
                        How can I improve engagement?
                      </button>
                    </>
                  ) : (
                    <>
                      <button className={classes.suggestionBtn} onClick={() => setChatInput('Which buttons get the most clicks?')}>
                        Which buttons get the most clicks?
                      </button>
                      <button className={classes.suggestionBtn} onClick={() => setChatInput('Are users clicking on the CTA buttons?')}>
                        Are users clicking on the CTA?
                      </button>
                      <button className={classes.suggestionBtn} onClick={() => setChatInput('What areas are being ignored?')}>
                        What areas are being ignored?
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {chatMessages.map((msg, i) => (
              <div key={i} className={`${classes.aiMessage} ${msg.role === 'user' ? classes.userMsg : classes.assistantMsg}`}>
                <div className={classes.msgIcon}>
                  <div className={classes.msgIconInner}>
                    <i className="material-icons">{msg.role === 'user' ? 'person' : 'psychology'}</i>
                  </div>
                </div>
                <div className={classes.msgContent}>
                  <div className={classes.msgText}>
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown className={classes.markdown}>{msg.content}</ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>

                  {msg.role === 'assistant' && (
                    <div className={classes.msgFeedback}>
                      {feedbackGiven[i] ? (
                        <span className={classes.feedbackThanks}>
                          <i className="material-icons">check_circle</i>
                          Thanks for your feedback!
                        </span>
                      ) : (
                        <>
                          <span className={classes.feedbackLabel}>Was this helpful?</span>
                          <button
                            className={`${classes.feedbackBtn} ${feedbackGiven[i] === 'thumbs_up' ? classes.feedbackActive : ''}`}
                            onClick={() => handleFeedback(i, 'thumbs_up')}
                            disabled={!!feedbackGiven[i]}
                            title="Yes, helpful"
                          >
                            <i className="material-icons">thumb_up</i>
                          </button>
                          <button
                            className={`${classes.feedbackBtn} ${feedbackGiven[i] === 'thumbs_down' ? classes.feedbackActive : ''}`}
                            onClick={() => handleFeedback(i, 'thumbs_down')}
                            disabled={!!feedbackGiven[i]}
                            title="Not helpful"
                          >
                            <i className="material-icons">thumb_down</i>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className={classes.aiTyping}>
                <div className={classes.msgIcon}>
                  <div className={classes.msgIconInner}>
                    <i className="material-icons">psychology</i>
                  </div>
                </div>
                <div className={classes.typingBubble}>
                  <div className={classes.typingDots}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className={classes.typingLabel}>
                    {activeTab === 'overview' ? 'Analyzing data...' : 'Analyzing clicks...'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleChatSubmit} className={classes.aiForm}>
            <div className={classes.aiInputWrap}>
              <textarea
                ref={chatTextareaRef}
                rows={1}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder={activeTab === 'overview' ? 'Ask me anything about this page...' : 'Ask about click patterns...'}
                className={classes.aiInput}
                disabled={chatLoading}
              />
              <button type="submit" className={classes.aiSubmit} disabled={chatLoading || !chatInput.trim()}>
                <i className="material-icons">arrow_upward</i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Genesis Light Theme colors
const colors = {
  bgPrimary: A.surface,
  bgSecondary: 'hsl(0, 0%, 98%)',
  bgTertiary: 'hsl(0, 0%, 95%)',
  bgHover: 'hsl(0, 0%, 93%)',
  textPrimary: 'hsl(0, 0%, 20%)',
  textSecondary: 'hsl(0, 0%, 40%)',
  textMuted: 'hsl(0, 0%, 50%)',
  textLight: 'hsl(0, 0%, 60%)',
  border: 'hsl(0, 0%, 88%)',
  borderLight: 'hsl(0, 0%, 92%)',
  accent: 'hsl(200, 73%, 33%)', // Genesis blue
  accentLight: 'hsl(200, 73%, 95%)',
  accentGreen: 'hsl(145, 50%, 40%)',
  accentOrange: 'hsl(30, 90%, 50%)',
  error: 'hsl(0, 70%, 50%)',
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    fontFamily: 'Roboto, sans-serif',
    overflow: 'hidden', // Prevent outer scrollbar
    [DARK]: { backgroundColor: S.chrome, color: S.fg },
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 30px',
    backgroundColor: colors.bgPrimary,
    borderBottom: `1px solid ${colors.border}`,
    [DARK]: { borderBottom: `1px solid ${S.line}` },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: '3px',
    color: colors.textSecondary,
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      background: colors.bgTertiary,
      color: colors.textPrimary,
      borderColor: colors.textMuted,
    },
    '& .material-icons': {
      fontSize: '20px',
    },
    [DARK]: {
      border: `1px solid ${S.line}`,
      color: S.muted,
      '&:hover': { background: S.chromeHover, color: S.fg, borderColor: S.muted },
    },
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  pageTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  pagePath: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: colors.textMuted,
    [DARK]: { color: S.muted },
  },
  versionTag: {
    padding: '2px 8px',
    backgroundColor: colors.accentGreen,
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '500',
    color: 'white',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  // Date Picker
  datePickerWrapper: {
    position: 'relative',
  },
  datePickerToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 12px',
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '3px',
    color: colors.textPrimary,
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all .2s',
    minWidth: '200px',
    '&:hover': {
      borderColor: colors.accent,
    },
    '& .material-icons': {
      fontSize: '18px',
      color: colors.textMuted,
    },
    [DARK]: {
      border: `1px solid ${S.line}`,
      color: S.fg,
      '&:hover': { borderColor: S.blue },
      '& .material-icons': { color: S.muted },
    },
  },
  dateRangeText: {
    flex: 1,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  datePickerDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '5px',
    width: '340px',
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden',
    [DARK]: { border: `1px solid ${S.line}` },
  },
  presetSection: {
    padding: '15px',
    borderBottom: `1px solid ${colors.borderLight}`,
    [DARK]: { borderBottom: `1px solid ${A.rowLine}` },
  },
  presetLabel: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: '10px',
    letterSpacing: '0.5px',
    [DARK]: { color: S.muted },
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
  },
  presetBtn: {
    padding: '8px 10px',
    backgroundColor: colors.bgSecondary,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '3px',
    color: colors.textSecondary,
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      backgroundColor: colors.bgTertiary,
      borderColor: colors.border,
      color: colors.textPrimary,
    },
    [DARK]: {
      backgroundColor: S.chrome,
      border: `1px solid ${A.rowLine}`,
      color: S.muted,
      '&:hover': { backgroundColor: S.chromeHover, borderColor: S.line, color: S.fg },
    },
  },
  presetActive: {
    backgroundColor: colors.accentLight,
    borderColor: colors.accent,
    color: colors.accent,
    fontWeight: '500',
    '&:hover': {
      backgroundColor: colors.accentLight,
      borderColor: colors.accent,
      color: colors.accent,
    },
    [DARK]: {
      backgroundColor: TONES.blue.bg,
      borderColor: S.blue,
      color: S.blue,
      '&:hover': { backgroundColor: TONES.blue.bg, borderColor: S.blue, color: S.blue },
    },
  },
  customSection: {
    padding: '15px',
    backgroundColor: colors.bgSecondary,
    [DARK]: { backgroundColor: S.chrome },
  },
  customLabel: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: '12px',
    letterSpacing: '0.5px',
    [DARK]: { color: S.muted },
  },
  dateInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '12px',
  },
  dateInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dateInputLabel: {
    fontSize: '11px',
    color: colors.textMuted,
    [DARK]: { color: S.muted },
  },
  dateInput: {
    padding: '10px 12px',
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '3px',
    color: colors.textPrimary,
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: colors.accent,
    },
    [DARK]: {
      border: `1px solid ${S.line}`,
      color: S.fg,
      '&:focus': { borderColor: S.blue },
    },
  },
  applyBtn: { width: '100%' },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: '3px',
    color: colors.textSecondary,
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      background: colors.bgTertiary,
      color: colors.textPrimary,
    },
    '& .material-icons': {
      fontSize: '18px',
    },
    [DARK]: {
      border: `1px solid ${S.line}`,
      color: S.muted,
      '&:hover': { background: S.chromeHover, color: S.fg },
    },
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 14px',
    backgroundColor: colors.accent,
    border: 'none',
    borderRadius: '3px',
    color: 'white',
    fontSize: '13px',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      backgroundColor: 'hsl(200, 73%, 28%)',
    },
    '& .material-icons': {
      fontSize: '16px',
    },
  },

  // Tab Bar
  tabBar: {
    display: 'flex',
    gap: '0',
    padding: '0 30px',
    backgroundColor: colors.bgPrimary,
    borderBottom: `1px solid ${colors.border}`,
    [DARK]: { borderBottom: `1px solid ${S.line}` },
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: colors.textMuted,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all .2s',
    '&:hover': {
      color: colors.textSecondary,
      backgroundColor: colors.bgSecondary,
    },
    '&[data-active="true"]': {
      color: colors.accent,
      borderBottomColor: colors.accent,
      fontWeight: '600',
    },
    '& .material-icons': {
      fontSize: '18px',
    },
    [DARK]: {
      color: S.muted,
      '&:hover': { color: S.muted, backgroundColor: S.chrome },
      '&[data-active="true"]': { color: S.blue, borderBottomColor: S.blue },
    },
  },

  // Content Wrapper (main content + AI chat side by side)
  contentWrapper: {
    display: 'flex',
    flex: 1,
    gap: '20px',
    padding: '20px 30px',
    overflow: 'hidden',
    minHeight: 0,
    '@media (max-width: 1200px)': {
      flexDirection: 'column',
    },
  },

  // Main Content
  mainContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    minWidth: 0, // Important for flex children to shrink properly
  },

  // Loading State
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: colors.textMuted,
    gap: '15px',
    '& .material-icons': {
      fontSize: '32px',
      animation: '$pulse 1.5s ease-in-out infinite',
    },
    [DARK]: { color: S.muted },
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${colors.border}`,
    borderTop: `3px solid ${colors.accent}`,
    borderRadius: '50%',
    animation: '$spin 1s linear infinite',
    [DARK]: { border: `3px solid ${S.line}`, borderTop: `3px solid ${S.blue}` },
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.4 },
    '50%': { opacity: 1 },
  },

  // Error State
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
    color: colors.error,
    '& .material-icons': {
      fontSize: '48px',
      marginBottom: '10px',
    },
    '& p': {
      margin: '0 0 15px 0',
      fontSize: '14px',
    },
    [DARK]: { color: S.red },
  },

  // Stats Row
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '18px 20px',
    backgroundColor: colors.bgPrimary,
    borderRadius: '3px',
    border: `1px solid ${colors.border}`,
    [DARK]: { border: `1px solid ${S.line}` },
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    backgroundColor: colors.accentLight,
    borderRadius: '3px',
    '& .material-icons': {
      fontSize: '22px',
      color: colors.accent,
    },
    [DARK]: {
      backgroundColor: TONES.blue.bg,
      '& .material-icons': { color: S.blue },
    },
  },
  statIconAlt: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    backgroundColor: 'hsl(30, 90%, 95%)',
    borderRadius: '3px',
    '& .material-icons': {
      fontSize: '22px',
      color: colors.accentOrange,
    },
    [DARK]: {
      backgroundColor: TONES.amber.bg,
      '& .material-icons': { color: S.amber },
    },
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '400',
    color: colors.textMuted,
    [DARK]: { color: S.muted },
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },

  // Charts Grid
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
  },

  // Panel (Chart Container)
  chartPanel: {
    backgroundColor: colors.bgPrimary,
    borderRadius: '3px',
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
    [DARK]: { border: `1px solid ${S.line}` },
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 18px',
    borderBottom: `1px solid ${colors.borderLight}`,
    background: colors.bgSecondary,
    [DARK]: { borderBottom: `1px solid ${A.rowLine}`, background: S.chrome },
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  panelBadge: {
    padding: '3px 10px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '12px',
    fontSize: '12px',
    color: colors.textMuted,
    [DARK]: { backgroundColor: S.chromeHover, color: S.muted },
  },
  panelBody: {
    padding: '18px',
  },
  panelNote: {
    fontSize: '12px',
    color: colors.textMuted,
    marginTop: '12px',
    [DARK]: { color: S.muted },
  },

  // Chart Rows (Daily Views)
  chartRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  chartLabel: {
    width: '50px',
    fontSize: '12px',
    color: colors.textMuted,
    textAlign: 'right',
    [DARK]: { color: S.muted },
  },
  chartBarBg: {
    flex: 1,
    height: '18px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '3px',
    overflow: 'hidden',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  chartBar: {
    height: '100%',
    backgroundColor: colors.accent,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  chartValue: {
    width: '40px',
    fontSize: '13px',
    fontWeight: '500',
    color: colors.textPrimary,
    textAlign: 'right',
    [DARK]: { color: S.fg },
  },

  // Device Rows
  deviceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  deviceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100px',
    '& .material-icons': {
      fontSize: '20px',
      color: colors.textMuted,
    },
    [DARK]: {
      '& .material-icons': { color: S.muted },
    },
  },
  deviceName: {
    fontSize: '14px',
    color: colors.textPrimary,
    textTransform: 'capitalize',
    [DARK]: { color: S.fg },
  },
  deviceBarBg: {
    flex: 1,
    height: '22px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '3px',
    overflow: 'hidden',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  deviceBar: {
    height: '100%',
    backgroundColor: colors.accentGreen,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  deviceStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '90px',
    justifyContent: 'flex-end',
  },
  deviceCount: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  devicePct: {
    fontSize: '13px',
    fontWeight: '400',
    color: colors.textMuted,
    minWidth: '38px',
    textAlign: 'right',
    [DARK]: { color: S.muted },
  },

  // Scroll Rows
  scrollRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  scrollMilestone: {
    width: '45px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    textAlign: 'right',
    [DARK]: { color: S.fg },
  },
  scrollBarBg: {
    flex: 1,
    height: '22px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '3px',
    overflow: 'hidden',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  scrollBar: {
    height: '100%',
    backgroundColor: colors.accentOrange,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  scrollPct: {
    width: '50px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    textAlign: 'right',
    [DARK]: { color: S.fg },
  },

  // Clicks List
  clicksList: {
    maxHeight: '280px',
    overflowY: 'auto',
  },
  clickItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    marginBottom: '8px',
    backgroundColor: colors.bgSecondary,
    borderRadius: '3px',
    border: `1px solid ${colors.borderLight}`,
    '&:last-child': {
      marginBottom: 0,
    },
    [DARK]: { backgroundColor: S.chrome, border: `1px solid ${A.rowLine}` },
  },
  clickRank: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: colors.accent,
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    flexShrink: 0,
  },
  clickDetails: {
    flex: 1,
    minWidth: 0,
  },
  clickText: {
    display: 'block',
    fontSize: '14px',
    color: colors.textPrimary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [DARK]: { color: S.fg },
  },
  clickSelector: {
    display: 'block',
    fontSize: '11px',
    color: colors.textMuted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '3px',
    [DARK]: { color: S.muted },
  },
  clickCount: {
    fontSize: '15px',
    fontWeight: '600',
    color: colors.accent,
    flexShrink: 0,
    [DARK]: { color: S.blue },
  },

  // Country Rows
  countryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  countryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '130px',
  },
  countryFlag: {
    fontSize: '18px',
    lineHeight: 1,
  },
  countryName: {
    fontSize: '14px',
    color: colors.textPrimary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [DARK]: { color: S.fg },
  },
  countryBarBg: {
    flex: 1,
    height: '22px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '3px',
    overflow: 'hidden',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  countryBar: {
    height: '100%',
    backgroundColor: 'hsl(220, 70%, 50%)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  countryStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '90px',
    justifyContent: 'flex-end',
  },
  countryCount: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  countryPct: {
    fontSize: '13px',
    fontWeight: '400',
    color: colors.textMuted,
    minWidth: '38px',
    textAlign: 'right',
    [DARK]: { color: S.muted },
  },

  // Traffic Source Rows
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sourceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100px',
    '& .material-icons': {
      fontSize: '20px',
      color: colors.textMuted,
    },
    [DARK]: {
      '& .material-icons': { color: S.muted },
    },
  },
  sourceName: {
    fontSize: '14px',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  sourceBarBg: {
    flex: 1,
    height: '22px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '3px',
    overflow: 'hidden',
    [DARK]: { backgroundColor: S.chromeHover },
  },
  sourceBar: {
    height: '100%',
    backgroundColor: 'hsl(280, 60%, 55%)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  sourceStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '90px',
    justifyContent: 'flex-end',
  },
  sourceCount: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  sourcePct: {
    fontSize: '13px',
    fontWeight: '400',
    color: colors.textMuted,
    minWidth: '38px',
    textAlign: 'right',
    [DARK]: { color: S.muted },
  },
  topReferrers: {
    marginTop: '20px',
    paddingTop: '15px',
    borderTop: `1px solid ${colors.borderLight}`,
    [DARK]: { borderTop: `1px solid ${A.rowLine}` },
  },
  referrerLabel: {
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: '10px',
    letterSpacing: '0.5px',
    [DARK]: { color: S.muted },
  },
  referrerItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    marginBottom: '6px',
    backgroundColor: colors.bgSecondary,
    borderRadius: '3px',
    '&:last-child': {
      marginBottom: 0,
    },
    [DARK]: { backgroundColor: S.chrome },
  },
  referrerDomain: {
    fontSize: '13px',
    color: colors.textPrimary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
    [DARK]: { color: S.fg },
  },
  referrerCount: {
    fontSize: '13px',
    fontWeight: '500',
    color: colors.accent,
    flexShrink: 0,
    [DARK]: { color: S.blue },
  },

  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: colors.textMuted,
    '& .material-icons': {
      fontSize: '40px',
      marginBottom: '10px',
      opacity: 0.5,
    },
    '& span': {
      fontSize: '14px',
    },
    [DARK]: { color: S.muted },
  },
  emptySubtext: {
    fontSize: '12px !important',
    marginTop: '5px',
    opacity: 0.7,
  },

  // Loading State
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 40px',
    color: colors.textMuted,
    '& span': {
      fontSize: '14px',
      marginTop: '15px',
    },
    [DARK]: { color: S.muted },
  },

  // Device Filter Section (Heatmap)
  deviceFilterSection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: colors.bgSecondary,
    borderRadius: '6px',
    border: `1px solid ${colors.borderLight}`,
    [DARK]: { backgroundColor: S.chrome, border: `1px solid ${A.rowLine}` },
  },
  deviceFilterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '13px',
    fontWeight: '500',
    color: colors.textSecondary,
    '& .material-icons': {
      fontSize: '18px',
      color: colors.textMuted,
    },
    [DARK]: {
      color: S.muted,
      '& .material-icons': { color: S.muted },
    },
  },
  deviceFilterButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  deviceFilterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: '1 1 auto',
    minWidth: '120px',
    '&:hover': {
      borderColor: colors.accent,
      backgroundColor: colors.bgTertiary,
    },
    '& .material-icons': {
      fontSize: '20px',
      color: colors.textMuted,
    },
    [DARK]: {
      border: `1px solid ${S.line}`,
      '&:hover': { borderColor: S.blue, backgroundColor: S.chromeHover },
      '& .material-icons': { color: S.muted },
    },
  },
  deviceFilterActive: {
    backgroundColor: colors.accentLight,
    borderColor: colors.accent,
    '& .material-icons': {
      color: colors.accent,
    },
    '& $deviceFilterBtnLabel': {
      color: colors.accent,
      fontWeight: '600',
    },
    [DARK]: {
      backgroundColor: TONES.blue.bg,
      borderColor: S.blue,
      '& .material-icons': { color: S.blue },
      '& $deviceFilterBtnLabel': { color: S.blue },
    },
  },
  deviceFilterBtnLabel: {
    fontSize: '13px',
    color: colors.textPrimary,
    flex: 1,
    [DARK]: { color: S.fg },
  },
  deviceFilterBtnCount: {
    fontSize: '12px',
    color: colors.textMuted,
    backgroundColor: colors.bgTertiary,
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '500',
    [DARK]: { color: S.muted, backgroundColor: S.chromeHover },
  },
  deviceFilterWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: 'hsl(45, 100%, 96%)',
    border: '1px solid hsl(45, 90%, 80%)',
    borderRadius: '4px',
    fontSize: '12px',
    color: 'hsl(30, 70%, 35%)',
    '& .material-icons': {
      fontSize: '16px',
      color: 'hsl(30, 90%, 50%)',
      flexShrink: 0,
      marginTop: '1px',
    },
    '& span': {
      lineHeight: 1.4,
    },
    [DARK]: {
      backgroundColor: TONES.amber.bg,
      border: `1px solid ${TONES.amber.bg}`,
      color: TONES.amber.fg,
      '& .material-icons': { color: S.amber },
    },
  },

  // AI Assistant Panel (permanently visible on right side)
  aiPanel: {
    display: 'flex',
    flexDirection: 'column',
    width: '420px',
    minWidth: '320px',
    maxWidth: '100%',
    height: '100%',
    backgroundColor: colors.bgPrimary,
    borderRadius: '3px',
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
    flexShrink: 0,
    '@media (max-width: 1200px)': {
      width: '100%',
      height: '500px',
      maxHeight: '50vh',
    },
    [DARK]: { border: `1px solid ${S.line}` },
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(-10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  aiHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    background: `linear-gradient(135deg, ${colors.accent} 0%, hsl(220, 70%, 45%) 100%)`,
    color: 'white',
  },
  aiHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  aiHeaderIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    '& .material-icons': {
      fontSize: '22px',
    },
  },
  aiHeaderTitle: {
    '& h4': {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
    },
    '& span': {
      fontSize: '12px',
      opacity: 0.85,
    },
  },
  aiHeaderActions: {
    display: 'flex',
    gap: '8px',
  },
  aiHeaderBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    transition: 'all .2s',
    '& .material-icons': {
      fontSize: '18px',
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.25)',
    },
  },
  aiMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    scrollBehavior: 'smooth',
    minHeight: 0, // Important for flex children to scroll properly
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.border,
      borderRadius: '3px',
      '&:hover': {
        background: colors.textMuted,
      },
    },
    [DARK]: {
      '&::-webkit-scrollbar-thumb': {
        background: S.line,
        '&:hover': { background: S.muted },
      },
    },
  },
  aiWelcome: {
    textAlign: 'center',
    padding: '30px 20px',
  },
  welcomeIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    background: `linear-gradient(135deg, ${colors.accentLight} 0%, hsl(200, 60%, 92%) 100%)`,
    borderRadius: '16px',
    marginBottom: '16px',
    '& .material-icons': {
      fontSize: '32px',
      color: colors.accent,
    },
    [DARK]: {
      background: TONES.blue.bg,
      '& .material-icons': { color: S.blue },
    },
  },
  welcomeTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  welcomeSubtitle: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: colors.textMuted,
    lineHeight: 1.5,
    [DARK]: { color: S.muted },
  },
  aiSuggestions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '320px',
    margin: '0 auto',
  },
  suggestionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.textMuted,
    marginBottom: '4px',
    [DARK]: { color: S.muted },
  },
  suggestionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: colors.bgSecondary,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '10px',
    color: colors.textSecondary,
    fontSize: '13px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all .2s ease',
    '&:hover': {
      backgroundColor: colors.bgPrimary,
      borderColor: colors.accent,
      color: colors.accent,
      transform: 'translateX(4px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    '&::before': {
      content: '"→"',
      fontSize: '14px',
      opacity: 0.5,
    },
    '&:hover::before': {
      opacity: 1,
    },
    [DARK]: {
      backgroundColor: S.chrome,
      border: `1px solid ${A.rowLine}`,
      color: S.muted,
      '&:hover': { backgroundColor: A.surface, borderColor: S.blue, color: S.blue },
    },
  },
  aiMessage: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    animation: '$fadeInUp 0.3s ease-out',
  },
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  userMsg: {
    flexDirection: 'row-reverse',
    '& $msgContent': {
      alignItems: 'flex-end',
    },
    '& $msgText': {
      background: `linear-gradient(135deg, ${colors.accent} 0%, hsl(220, 70%, 45%) 100%)`,
      color: 'white',
      borderRadius: '16px 16px 4px 16px',
    },
  },
  assistantMsg: {
    '& $msgText': {
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '16px 16px 16px 4px',
    },
    [DARK]: {
      '& $msgText': { backgroundColor: S.chrome, border: `1px solid ${A.rowLine}` },
    },
  },
  msgIcon: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '4px',
  },
  msgIconInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: colors.bgTertiary,
    borderRadius: '12px',
    '& .material-icons': {
      fontSize: '20px',
      color: colors.textMuted,
    },
    [DARK]: {
      backgroundColor: S.chromeHover,
      '& .material-icons': { color: S.muted },
    },
  },
  msgContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '85%',
    minWidth: '100px',
  },
  msgText: {
    padding: '14px 18px',
    fontSize: '14px',
    lineHeight: 1.65,
    color: colors.textPrimary,
    [DARK]: { color: S.fg },
  },
  msgTime: {
    fontSize: '11px',
    color: colors.textMuted,
    marginTop: '6px',
    paddingLeft: '2px',
    [DARK]: { color: S.muted },
  },
  markdown: {
    '& p': {
      margin: '0 0 12px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& h1, & h2, & h3, & h4': {
      margin: '16px 0 8px 0',
      fontWeight: 600,
      color: colors.textPrimary,
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h2': {
      fontSize: '16px',
      borderBottom: `1px solid ${colors.borderLight}`,
      paddingBottom: '6px',
    },
    '& h3': {
      fontSize: '14px',
    },
    '& h4': {
      fontSize: '13px',
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
    },
    '& li': {
      marginBottom: '4px',
    },
    '& strong': {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    '& code': {
      backgroundColor: colors.bgTertiary,
      padding: '2px 6px',
      borderRadius: '3px',
      fontSize: '13px',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: colors.bgTertiary,
      padding: '12px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '8px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& a': {
      color: colors.accent,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& blockquote': {
      margin: '8px 0',
      paddingLeft: '12px',
      borderLeft: `3px solid ${colors.accent}`,
      color: colors.textSecondary,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${colors.borderLight}`,
      margin: '12px 0',
    },
    [DARK]: {
      '& h1, & h2, & h3, & h4': { color: S.fg },
      '& h2': { borderBottom: `1px solid ${A.rowLine}` },
      '& strong': { color: S.fg },
      '& code': { backgroundColor: S.chromeHover },
      '& pre': { backgroundColor: S.chromeHover },
      '& a': { color: S.blue },
      '& blockquote': { borderLeft: `3px solid ${S.blue}`, color: S.muted },
      '& hr': { borderTop: `1px solid ${A.rowLine}` },
    },
  },
  msgFeedback: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '10px',
  },
  feedbackLabel: {
    fontSize: '12px',
    color: colors.textMuted,
    [DARK]: { color: S.muted },
  },
  feedbackBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    background: 'transparent',
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all .2s ease',
    '& .material-icons': {
      fontSize: '18px',
      color: colors.textMuted,
    },
    '&:hover:not(:disabled)': {
      backgroundColor: colors.bgTertiary,
      borderColor: colors.accent,
      transform: 'scale(1.05)',
      '& .material-icons': {
        color: colors.accent,
      },
    },
    '&:disabled': {
      cursor: 'default',
    },
    [DARK]: {
      border: `1px solid ${A.rowLine}`,
      '& .material-icons': { color: S.muted },
      '&:hover:not(:disabled)': {
        backgroundColor: S.chromeHover,
        borderColor: S.blue,
        '& .material-icons': { color: S.blue },
      },
    },
  },
  feedbackActive: {
    backgroundColor: colors.accentLight,
    borderColor: colors.accent,
    '& .material-icons': {
      color: colors.accent,
    },
    [DARK]: {
      backgroundColor: TONES.blue.bg,
      borderColor: S.blue,
      '& .material-icons': { color: S.blue },
    },
  },
  feedbackThanks: {
    fontSize: '12px',
    color: colors.accentGreen,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '& .material-icons': {
      fontSize: '14px',
    },
    [DARK]: { color: S.green },
  },
  aiTyping: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    paddingTop: '4px',
  },
  typingBubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '200px',
  },
  typingDots: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '14px 18px',
    backgroundColor: colors.bgSecondary,
    borderRadius: '16px 16px 16px 4px',
    border: `1px solid ${colors.borderLight}`,
    '& span': {
      width: '8px',
      height: '8px',
      backgroundColor: colors.accent,
      borderRadius: '50%',
      animation: '$typingPulse 1.4s infinite ease-in-out both',
      '&:nth-child(1)': { animationDelay: '-0.32s' },
      '&:nth-child(2)': { animationDelay: '-0.16s' },
      '&:nth-child(3)': { animationDelay: '0s' },
    },
    [DARK]: { backgroundColor: S.chrome, border: `1px solid ${A.rowLine}` },
  },
  typingLabel: {
    fontSize: '12px',
    color: colors.textMuted,
    paddingLeft: '4px',
    [DARK]: { color: S.muted },
  },
  '@keyframes typingPulse': {
    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
    '40%': { transform: 'scale(1)', opacity: 1 },
  },
  aiForm: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: colors.bgSecondary,
    [DARK]: { borderTop: `1px solid ${S.line}`, backgroundColor: S.chrome },
  },
  aiInputWrap: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch', // important for textarea growth
  },
  aiInput: {
    width: '100%',
    padding: '14px 18px',
    paddingRight: '50px',
    backgroundColor: colors.bgPrimary,
    border: `2px solid ${colors.borderLight}`,
    borderRadius: '12px',
    color: colors.textPrimary,
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: '20px',

    // textarea-specific
    resize: 'none',
    overflowY: 'hidden', // JS flips to auto when needed
    minHeight: '48px',
    maxHeight: '180px',
    boxSizing: 'border-box',

    transition: 'all .2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: colors.accent,
      boxShadow: `0 0 0 3px ${colors.accentLight}`,
    },
    '&::placeholder': {
      color: colors.textMuted,
    },
    '&:disabled': {
      backgroundColor: colors.bgTertiary,
    },
    [DARK]: {
      border: `2px solid ${A.rowLine}`,
      color: S.fg,
      '&:focus': { borderColor: S.blue, boxShadow: `0 0 0 3px ${TONES.blue.bg}` },
      '&::placeholder': { color: S.muted },
      '&:disabled': { backgroundColor: S.chromeHover },
    },
  },
  aiSubmit: {
    position: 'absolute',
    right: '6px',
    bottom: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '37px',
    height: '37px',
    background: `linear-gradient(135deg, ${colors.accent} 0%, hsl(220, 70%, 45%) 100%)`,
    border: 'none',
    borderRadius: '50%',
    color: 'white',
    cursor: 'pointer',
    transition: 'all .2s ease',
    '& .material-icons': {
      fontSize: '20px',
    },
    '&:hover:not(:disabled)': {
      boxShadow: '0 2px 8px rgba(45, 125, 170, 0.4)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },

  // Version Scope Toggle
  versionScopeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 14px',
    backgroundColor: colors.bgSecondary,
    border: `1px solid ${colors.border}`,
    borderRadius: '3px',
    [DARK]: { backgroundColor: S.chrome, border: `1px solid ${S.line}` },
  },
  versionScopeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: colors.textMuted,
    '& .material-icons': {
      fontSize: '16px',
    },
    [DARK]: { color: S.muted },
  },
  versionScopeButtons: {
    display: 'flex',
    gap: '6px',
  },
  versionScopeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: '500',
    color: colors.textMuted,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '& .material-icons': {
      fontSize: '16px',
    },
    '&:hover': {
      backgroundColor: colors.bgTertiary,
      borderColor: colors.border,
    },
    [DARK]: {
      border: `1px solid ${A.rowLine}`,
      color: S.muted,
      '&:hover': { backgroundColor: S.chromeHover, borderColor: S.line },
    },
  },
  versionScopeActive: {
    backgroundColor: colors.accentLight,
    borderColor: colors.accent,
    color: colors.accent,
    '& .material-icons': {
      color: colors.accent,
    },
    '&:hover': {
      backgroundColor: colors.accentLight,
      borderColor: colors.accent,
    },
    [DARK]: {
      backgroundColor: TONES.blue.bg,
      borderColor: S.blue,
      color: S.blue,
      '& .material-icons': { color: S.blue },
      '&:hover': { backgroundColor: TONES.blue.bg, borderColor: S.blue },
    },
  },

  // Tooltip for info icons
  tooltipWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover $tooltip': {
      visibility: 'visible',
      opacity: 1,
    },
  },
  tooltip: {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    top: '24px',
    left: '50%',
    transform: 'translateX(-50%) translateY(8px)',
    backgroundColor: colors.textPrimary,
    color: colors.bgPrimary,
    fontSize: '12px',
    lineHeight: '1.4',
    padding: '8px 12px',
    borderRadius: '4px',
    whiteSpace: 'normal',
    width: '220px',
    textAlign: 'left',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
    pointerEvents: 'none',
    // Tooltip arrow
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: '6px',
      borderStyle: 'solid',
      borderColor: `transparent transparent ${colors.textPrimary} transparent`,
    },
    [DARK]: {
      backgroundColor: S.fg,
      '&::after': { borderColor: `transparent transparent ${S.fg} transparent` },
    },
  },
});

export default PageAnalytics;
